import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { find } from 'lodash';
import { Table } from 'components/Base/Table';
import {
  TAttendanceListUI,
  TRegistrantUI,
  TAttendancesGetUI,
  ERegistrantsColumnKey,
  EAttendanceStatus,
  EAttendanceColumnKey,
} from 'data/types/registrants.types';
import { EWorkshopLanguage } from 'data/types/generic.types';
import { workshopsApi } from 'redux/apiSlice/workshops.slice';
import { useParams } from 'react-router-dom';
import {
  useGetRegistrantsQuery,
  useExportAttendanceMutation,
  useUpdateWorkshopRegistrantNotificationMutation,
} from 'redux/apiSlice/registrants.slice';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import AttendanceBanner from 'components/Shared/AttendanceBanner';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { downloadFile } from 'redux/services/fileServices';
import { TAttendanceListProps } from './AttendanceList.types';
import useGetData from './AttendanceList.data';
import useAgeGroupLanguageFilter from '../../RegistrantsTab/RegistrantsList/useAgeGroupLanguageFilter';

const EXPORT_COLUMN_HEADERS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  photo: 'Photo',
  childrenNames: 'Child(ren) Name(s)',
  ageGroup: 'Age Group',
  questionAnswer: 'Question Answer',
  materialsLanguage: 'Materials Language',
  attendance: 'Attendance',
  kitsReceived: 'Kits Received',
  binderReceived: 'Binder Received',
};

const escapeCsvField = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const extractAttendanceColumnValue = (
  row: TAttendanceListUI,
  column: string,
): string => {
  switch (column) {
    case 'firstName':
      return row.firstName || '';
    case 'lastName':
      return row.lastName || '';
    case 'photo':
      if (row.photo === null) return '';
      return row.photo ? 'Yes' : 'No';
    case 'childrenNames':
      return row.childrenNames?.join(', ') || '';
    case 'ageGroup': {
      const parts: string[] = [];
      row.englishAgeGroups?.forEach(g => {
        parts.push(`( E: ${g.ageGroup} )`);
      });
      row.spanishAgeGroups?.forEach(g => {
        parts.push(`( S: ${g.ageGroup} )`);
      });
      return parts.join(', ');
    }
    case 'questionAnswer':
      return row.customQuestionAnswer || '';
    case 'materialsLanguage':
      return row.materialsLanguage || '';
    case 'attendance':
      return row.attendance || '';
    case 'kitsReceived':
      return String(row.kits || 0);
    case 'binderReceived':
      if (!row.binders?.requested) return 'Not Required';
      return row.binders.received ? 'Yes' : 'No';
    default:
      return '';
  }
};

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EAttendanceColumnKey.FIRST_NAME]: 'firstName',
  [EAttendanceColumnKey.LAST_NAME]: 'lastName',
  [EAttendanceColumnKey.PHOTO]: 'photo',
  childrenNames: 'childrenNames',
  [ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP]: 'ageGroup',
  customQuestionAnswer: 'questionAnswer',
  [EAttendanceColumnKey.MATERIALS_LANGUAGE_FILTER]: 'materialsLanguage',
  attendance: 'attendance',
  kits: 'kitsReceived',
  binders: 'binderReceived',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EAttendanceColumnKey.FIRST_NAME]: 'firstName',
  [EAttendanceColumnKey.LAST_NAME]: 'lastName',
  [EAttendanceColumnKey.PHOTO]: 'photo',
};

const AttendanceList: FC<TAttendanceListProps> = ({
  workshopId,
  workshopStatus,
  workshopType,
  title,
  partType = '',
  sessionDateTime,
  sessions,
  customQuestion,
}) => {
  const [attendanceData, setAttendanceData] = useState<TAttendanceListUI[]>([]);
  const { showToastNotification } = useShowToastNotification();
  const [updateNotification] =
    useUpdateWorkshopRegistrantNotificationMutation();

  const handleNotificationToggle = async (
    record: TAttendanceListUI,
    checked: boolean,
  ) => {
    setAttendanceData(prev =>
      prev.map(item =>
        item.registrantId === record.registrantId
          ? { ...item, notificationsEnabled: checked }
          : item,
      ),
    );
    try {
      await updateNotification({
        workshopId,
        registrantId: record.registrantId,
        notificationDisabled: !checked,
      }).unwrap();
      showToastNotification({
        message: `Notifications ${
          checked ? 'enabled' : 'disabled'
        } successfully`,
        type: ENotificationType.SUCCESS,
      });
    } catch {
      setAttendanceData(prev =>
        prev.map(item =>
          item.registrantId === record.registrantId
            ? { ...item, notificationsEnabled: !checked }
            : item,
        ),
      );
      showToastNotification({
        message: 'Failed to update notification preference',
        type: ENotificationType.ERROR,
      });
    }
  };

  const { defaultColumns, defaultSortParam } = useGetData({
    workshopId,
    workshopStatus,
    workshopType,
    sessions,
    onNotificationToggle: handleNotificationToggle,
  });

  const filteredDefaultColumns = useMemo(
    () =>
      customQuestion
        ? defaultColumns
        : defaultColumns.filter(
            col => col.dataIndex !== 'customQuestionAnswer',
          ),
    [defaultColumns, customQuestion],
  );

  const storageKey = `${ETableStoragePaths.ATTENDANCE_LIST}-${workshopId}`;

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TAttendanceListUI>({
    storagePath: storageKey,
    defaultColumns: filteredDefaultColumns,
    defaultSortColumn: defaultSortParam,
  });

  const params = useParams();
  const { id } = params as { id: string };

  const { data: workshopDetailsCachedData } =
    workshopsApi.endpoints.getWorkshopById.useQueryState(
      {
        id,
      },
      { skip: !id },
    );

  const workshopClasses = workshopDetailsCachedData?.classes.value || [];

  const ageGroupLanguageFilters = useAgeGroupLanguageFilter(workshopClasses);
  useEffect(() => {
    if (ageGroupLanguageFilters) {
      setColumns(
        columns.map(item => {
          if (
            item.key ===
              ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP &&
            ageGroupLanguageFilters
          ) {
            item.filters = ageGroupLanguageFilters;
          }
          return item;
        }),
      );
    }
  }, [workshopClasses]);

  const { data: registrantsData, isFetching } = useGetRegistrantsQuery(
    {
      page: 0,
      pageSize: 2000,
      sortColumn,
      filters: selectedFilters,
      workshopId,
    },
    {
      skip: !workshopId,
    },
  );

  const processAttendanceData = (content: TRegistrantUI[]) => {
    if (!content?.length) return [];

    return content
      .filter(item => item.status !== 'UNREGISTERED')
      .map((item: TRegistrantUI) => {
        const {
          firstName,
          lastName,
          childrenNames,
          spanishAgeGroups,
          englishAgeGroups,
          attendances,
          allowedTakePicture,
          countOfChildren,
          orientationKit,
          registrantId,
          status,
          materialsLanguage,
          customQuestionAnswer,
        } = item;

        const currentAttendance = find(attendances, {
          partType,
        }) as TAttendancesGetUI;
        const attendance =
          currentAttendance?.attendanceStatus === EAttendanceStatus.E_LEARNING
            ? EAttendanceStatus.ATTENDED
            : currentAttendance?.attendanceStatus;

        const currentAttendanceSessionId =
          currentAttendance?.sessionAttendanceId;

        return {
          firstName,
          lastName,
          photo: allowedTakePicture,
          childrenNames,
          spanishAgeGroups,
          englishAgeGroups,
          attendance,
          countOfChildren,
          kits: currentAttendance?.kitCount || 0,
          binders: {
            received: orientationKit ? orientationKit.received : false,
            requested: orientationKit ? orientationKit.requested : false,
          },
          attendances,
          registrantId,
          currentAttendanceSessionId,
          sessionDateTime,
          sessions,
          status,
          materialsLanguage,
          customQuestionAnswer,
          notificationsEnabled: item.notificationsEnabled ?? true,
          minimalRegistration: !!item.minimalRegistration,
        };
      });
  };

  useEffect(() => {
    if (registrantsData) {
      const data = processAttendanceData(registrantsData.content);

      setAttendanceData(data as TAttendanceListUI[]);
    }
  }, [registrantsData]);

  const [exportAttendance, { isLoading: isExporting }] =
    useExportAttendanceMutation();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[storageKey] as TColumnsState[];
    const exportColumns = columnsState
      ? columnsState
          .filter(col => col.show)
          .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
          .filter(Boolean)
      : Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    return customQuestion
      ? exportColumns
      : exportColumns.filter(col => col !== 'questionAnswer');
  }, [storageKey, customQuestion]);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {
        workshopId,
        partType,
      };
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      const groupedFilter =
        selectedFilters?.[
          ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP
        ];
      if (groupedFilter?.length) {
        const allLanguages = Object.values(EWorkshopLanguage) as string[];
        const parentGroupValues = [
          ERegistrantsColumnKey.AGE_GROUP as string,
          ERegistrantsColumnKey.LANGUAGE as string,
        ];
        const ageGroups = groupedFilter.filter(
          v =>
            !allLanguages.includes(v as string) &&
            !parentGroupValues.includes(v as string),
        );
        if (ageGroups.length) {
          filters.ageGroups = ageGroups;
        }
      }
      const languageFilter =
        selectedFilters?.[EAttendanceColumnKey.MATERIALS_LANGUAGE_FILTER];
      if (languageFilter?.length) {
        filters.materialsLanguages = languageFilter;
      }
      return filters;
    },
    [workshopId, partType, selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const hasTreeFilter = useMemo(
    () =>
      !!selectedFilters?.[
        ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP
      ]?.length,
    [selectedFilters],
  );

  const exportClientSideCsv = useCallback(
    (data: TAttendanceListUI[], cols: string[]) => {
      const headers = cols.map(col => EXPORT_COLUMN_HEADERS[col] || col);
      const rows = data.map(row =>
        cols
          .map(col => escapeCsvField(extractAttendanceColumnValue(row, col)))
          .join(','),
      );
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const dateStr = new Date().toISOString().split('T')[0];
      downloadFile(blob, `Attendance_Export_${dateStr}.csv`);
    },
    [],
  );

  const handleExportAll = useCallback(async () => {
    if (!partType) {
      showToastNotification({
        message: 'Part type is required to export attendance.',
        type: ENotificationType.ERROR,
      });
      return;
    }

    const exportColumns = getVisibleExportColumns();

    // When age group / language tree filters are active, export from
    // the already-filtered table data because the BE export endpoint
    // does not properly apply these filters.
    if (hasTreeFilter) {
      exportClientSideCsv(attendanceData, exportColumns);
      return;
    }

    try {
      const sort = buildExportSort();
      await exportAttendance({
        columns: exportColumns,
        filters: buildExportFilters(),
        ...(sort && { sort }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    partType,
    exportAttendance,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
    hasTreeFilter,
    exportClientSideCsv,
    attendanceData,
  ]);

  const handleExportSelected = useCallback(async () => {
    if (!partType) {
      showToastNotification({
        message: 'Part type is required to export attendance.',
        type: ENotificationType.ERROR,
      });
      return;
    }
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    const exportColumns = getVisibleExportColumns();
    const selectedIds = new Set(selectedRowsKeys.map(key => String(key)));
    const selectedData = attendanceData.filter(row =>
      selectedIds.has(row.registrantId),
    );
    exportClientSideCsv(selectedData, exportColumns);
  }, [
    partType,
    selectedRows,
    getVisibleExportColumns,
    showToastNotification,
    exportClientSideCsv,
    attendanceData,
  ]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  // GROW-4332: read the current session's window state from any row's matching
  // attendance — BE sends the same state for every registrant of a given part.
  const currentSessionWindow = useMemo(() => {
    const firstAttendance = registrantsData?.content
      ?.flatMap(r => r.attendances || [])
      .find(a => a.partType === partType);
    return {
      attendanceState: firstAttendance?.attendanceState,
      attendanceOpensAt: firstAttendance?.attendanceOpensAt,
      attendanceClosesAt: firstAttendance?.attendanceClosesAt,
    };
  }, [registrantsData, partType]);

  return (
    <>
      <AttendanceBanner
        attendanceState={currentSessionWindow.attendanceState}
        attendanceOpensAt={currentSessionWindow.attendanceOpensAt}
        attendanceClosesAt={currentSessionWindow.attendanceClosesAt}
        textKeyPrefix="descriptions.admin-portal.workshops.details.attendance-list"
      />
      <Table<TAttendanceListUI>
        tableTitle={title}
        tabHeight={44}
        onFiltersReset={onFiltersReset}
        hasFilters={hasFilters}
        currentPage={page}
        columns={columns}
        headerActions={[exportAction]}
        dataSource={attendanceData}
        loading={isFetching}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalDataCount={registrantsData?.totalElements}
        onChange={handleTableChange}
        rowKey={record => record.registrantId}
        storagePath={storageKey}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    </>
  );
};

export default AttendanceList;
